import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import {
  AlertTriangle, ArrowUpDown, CheckCircle2, Info, Loader2, Search, X, XCircle, ChevronDown,
} from 'lucide-react'
import { STATUS_LABEL, STATUS_TONE } from '@/lib/domain'

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
export function Card({ title, subtitle, actions, children, className, bodyClass }: {
  title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode
  children: ReactNode; className?: string; bodyClass?: string
}) {
  return (
    <section className={clsx('card', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            {title && <h2 className="text-base font-semibold text-slate-800">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
          {actions && <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2 [&>*]:max-w-full">{actions}</div>}
        </header>
      )}
      <div className={clsx('card-pad', bodyClass)}>{children}</div>
    </section>
  )
}

export function Field({ label, hint, error, required, children, className }: {
  label?: string; hint?: ReactNode; error?: string | null; required?: boolean
  children: ReactNode; className?: string
}) {
  return (
    <div className={className}>
      {label && (
        <label className="label">
          {label}{required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      {children}
      {error ? <p className="mt-1 text-xs font-medium text-red-600">{error}</p>
             : hint ? <p className="hint">{hint}</p> : null}
    </div>
  )
}

export const Input = (p: React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) => {
  const { invalid, className, ...rest } = p
  return <input {...rest} className={clsx('field', invalid && 'field-error', className)} />
}

export const Textarea = (p: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...p} className={clsx('field', p.className)} />
)

export function Select({ options, placeholder, className, ...rest }: {
  options: Array<{ value: string; label: string }>
  placeholder?: string
} & React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    // max-w-full (13-10-2026): a drop-down is as wide as its longest option,
    // and a list of long site names made one 350 px wide on a 360 px phone.
    <div className="relative max-w-full">
      <select {...rest} className={clsx('field max-w-full appearance-none pr-9', className)}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  )
}

export function StatusPill({ status, className }: { status: string; className?: string }) {
  const tone = STATUS_TONE[status] ?? 'grey'
  return (
    <span className={clsx(`pill-${tone}`, className)} title={STATUS_LABEL[status] ?? status}>
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

export const Spinner = ({ className }: { className?: string }) => (
  <Loader2 className={clsx('h-4 w-4 animate-spin', className)} />
)

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
      <Spinner /> {label}
    </div>
  )
}

export function EmptyState({ icon, title, message, action }: {
  icon?: ReactNode; title: string; message?: ReactNode; action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
      <div className="text-slate-300">{icon ?? <Info className="h-9 w-9" />}</div>
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {message && <p className="max-w-md text-sm text-slate-500">{message}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

export function Alert({ tone = 'info', title, children, onClose }: {
  tone?: 'info' | 'success' | 'warning' | 'error'
  title?: ReactNode; children?: ReactNode; onClose?: () => void
}) {
  const map = {
    // 30-09-2026, after the branding: a box that needs the reader's attention
    // must stand out from the calm page. Warning = a graphite card with a 4px
    // dark bar (Board-approved; the palette has no amber). Info = a thinner
    // sage bar, quieter than a warning so the two are told apart at a glance.
    info: ['bg-sky-50 border-sky-200 border-l-[3px] border-l-sky-600 text-sky-900', <Info key="i" className="h-4 w-4 shrink-0 text-sky-600" />],
    success: ['bg-emerald-50 border-emerald-200 text-emerald-900', <CheckCircle2 key="s" className="h-4 w-4 shrink-0" />],
    warning: ['bg-amber-50 border-amber-200 border-l-4 border-l-amber-600 text-amber-900', <AlertTriangle key="w" className="h-4 w-4 shrink-0 text-amber-600" />],
    error: ['bg-red-50 border-red-200 text-red-900', <XCircle key="e" className="h-4 w-4 shrink-0" />],
  } as const
  const [cls, icon] = map[tone]
  return (
    <div className={clsx('flex items-start gap-2 rounded-lg border px-3 py-2 text-sm', cls)}>
      <span className="mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={clsx(title && 'mt-0.5')}>{children}</div>}
      </div>
      {onClose && (
        <button onClick={onClose} className="rounded p-0.5 hover:bg-black/5" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

export function Modal({ open, onClose, title, subtitle, children, footer, wide }: {
  open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode
  children: ReactNode; footer?: ReactNode; wide?: boolean
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4">
      <div className={clsx(
        'flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-xl',
        wide ? 'sm:max-w-4xl' : 'sm:max-w-lg',
      )}>
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-800">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">{children}</div>
        {footer && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}

export function SearchInput({ value, onChange, placeholder = 'Search…', className }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string
}) {
  return (
    <div className={clsx('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        className="field pl-9"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100"
          aria-label="Clear"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

export function Stat({ label, value, sub, tone = 'default', icon }: {
  label: string; value: ReactNode; sub?: ReactNode
  tone?: 'default' | 'good' | 'warn' | 'bad'; icon?: ReactNode
}) {
  const toneCls = {
    default: 'text-slate-800', good: 'text-emerald-700', warn: 'text-amber-700', bad: 'text-red-700',
  }[tone]
  // Branding 10-10-2026: the icon sits on a soft tile in the card's meaning -
  // OVAL Blue for a plain figure, teal for healthy, bronze for attention, muted
  // red for bad - as on the Board's sample dashboard. Layout is unchanged.
  const tileCls = {
    default: 'bg-oval-50 text-oval-700', good: 'bg-teal-50 text-teal-700',
    warn: 'bg-amber-50 text-amber-600', bad: 'bg-red-50 text-red-600',
  }[tone]
  return (
    <div className="card card-pad">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        {icon && <span className={clsx('-mr-1 -mt-1 rounded-lg p-1.5', tileCls)}>{icon}</span>}
      </div>
      {/* Phones (13-10-2026): a rupee figure in crores did not fit half a
          360 px screen at text-2xl and spilled past its tile. One step
          smaller on a phone, and allowed to wrap rather than spill; from a
          tablet up it is exactly as before. */}
      <p className={clsx('mt-1.5 break-words text-lg font-bold tabular-nums sm:text-2xl', toneCls)}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------
interface Toast { id: number; tone: 'info' | 'success' | 'warning' | 'error'; message: ReactNode }
interface ToastApi {
  push: (tone: Toast['tone'], message: ReactNode) => void
  success: (m: ReactNode) => void
  error: (m: ReactNode) => void
  info: (m: ReactNode) => void
  warn: (m: ReactNode) => void
}
const ToastCtx = createContext<ToastApi>(null as unknown as ToastApi)
export const useToast = () => useContext(ToastCtx)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback((tone: Toast['tone'], message: ReactNode) => {
    const id = Date.now() + Math.random()
    setToasts((t) => [...t, { id, tone, message }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 9000 : 4500)
  }, [])

  const api = useMemo<ToastApi>(() => ({
    push,
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
    warn: (m) => push('warning', m),
  }), [push])

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="no-print pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 px-4 sm:bottom-auto sm:right-4 sm:top-4 sm:items-end">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto w-full max-w-sm shadow-lg">
            <Alert tone={t.tone} onClose={() => setToasts((x) => x.filter((y) => y.id !== t.id))}>
              {t.message}
            </Alert>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

// ---------------------------------------------------------------------------
// Confirm dialog
// ---------------------------------------------------------------------------
export function Confirm({ open, title, message, confirmLabel = 'Confirm', tone = 'primary', onConfirm, onCancel, requireReason, reasonLabel }: {
  open: boolean; title: string; message: ReactNode; confirmLabel?: string
  tone?: 'primary' | 'danger' | 'warning' | 'success'
  onConfirm: (reason?: string) => void | Promise<void>; onCancel: () => void
  requireReason?: boolean; reasonLabel?: string
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (open) { setReason(''); setBusy(false) } }, [open])

  const btnClass = { primary: 'btn-primary', danger: 'btn-danger', warning: 'btn-warning', success: 'btn-success' }[tone]

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className={btnClass}
            disabled={busy || (requireReason && reason.trim().length < 3)}
            onClick={async () => { setBusy(true); try { await onConfirm(reason.trim()) } finally { setBusy(false) } }}
          >
            {busy && <Spinner />} {confirmLabel}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-slate-600">
        <div>{message}</div>
        {requireReason && (
          <Field label={reasonLabel ?? 'Reason'} required hint="This is recorded in the audit trail and shown to the originator.">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Sortable tables
//
// Every register in this system is a list somebody needs to re-order: the
// Head of Stores wants the oldest goods receipt first, procurement wants the
// biggest purchase order first, the CXO wants everything by site. Rather than
// building sort into each screen, a list declares its columns once and gets
// clickable headers for nothing.
// ---------------------------------------------------------------------------
export type SortDir = 'asc' | 'desc'

/** How a column's value should be compared. */
export type SortKind = 'text' | 'number' | 'date'

export interface SortState { key: string | null; dir: SortDir }

export function useSort(initialKey: string | null = null, initialDir: SortDir = 'desc') {
  const [sort, setSort] = useState<SortState>({ key: initialKey, dir: initialDir })

  /** Click once to sort, click again to reverse. */
  const toggle = useCallback((key: string) => {
    setSort((s) => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])

  return { sort, toggle, setSort }
}

const EMPTY = ['', null, undefined]

/**
 * Sort rows by the active column.
 *
 * Blanks always sink to the bottom whichever way the column is pointing —
 * a row with no delivery date is not "earliest", it is simply unknown.
 */
export function sortRows<T extends Record<string, any>>(
  rows: T[],
  sort: SortState,
  kinds: Record<string, SortKind> = {},
): T[] {
  if (!sort.key) return rows
  const key = sort.key

  // A column whose id does not match any property sorts nothing at all, and
  // does it silently — the user clicks and the table simply refuses to move.
  // That is exactly how the Quantity and Change columns shipped broken, so it
  // now says so out loud while developing.
  if (import.meta.env.DEV && rows.length > 0 && !(key in rows[0])) {
    // eslint-disable-next-line no-console
    console.warn(
      `[sortRows] No column "${key}" on these rows — the Th id does not match the data. ` +
      `Available: ${Object.keys(rows[0]).join(', ')}`,
    )
  }
  const kind = kinds[key] ?? 'text'
  const factor = sort.dir === 'asc' ? 1 : -1

  return [...rows].sort((a, b) => {
    const x = a[key]
    const y = b[key]
    const xEmpty = EMPTY.includes(x as any)
    const yEmpty = EMPTY.includes(y as any)
    if (xEmpty && yEmpty) return 0
    if (xEmpty) return 1
    if (yEmpty) return -1

    if (kind === 'number') return (Number(x) - Number(y)) * factor
    if (kind === 'date') {
      return (new Date(x as string).getTime() - new Date(y as string).getTime()) * factor
    }
    return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }) * factor
  })
}

/** A clickable table heading. Drop-in replacement for <th>. */
export function Th({ id, sort, onSort, children, className, align }: {
  /** The row property this column sorts on. Omit to make the column static. */
  id?: string
  sort?: SortState
  onSort?: (key: string) => void
  children?: ReactNode
  className?: string
  align?: 'left' | 'right'
}) {
  const active = !!id && sort?.key === id
  if (!id || !onSort) return <th className={className}>{children}</th>

  return (
    <th className={clsx(className, 'cursor-pointer select-none hover:text-slate-800')}
        onClick={() => onSort(id)}
        title="Click to sort, click again to reverse"
        aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <span className={clsx('inline-flex items-center gap-1', align === 'right' && 'flex-row-reverse')}>
        {children}
        <ArrowUpDown className={clsx(
          'h-3 w-3 shrink-0 transition',
          active ? 'text-oval-700' : 'text-slate-300',
          active && sort!.dir === 'desc' && 'rotate-180',
        )} />
      </span>
    </th>
  )
}
